import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import { fetchCollaboratorOptions } from "../services/collaborators";
import Layout from "../components/Layout";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { ArrowLeft, Plus } from "lucide-react";
import { getAssetLocations } from "../services/assetLocations";
import LocationCatalogModal from "../components/assets/LocationCatalogModal";

const NewAsset = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // Colaboradores para o listbox (usuário do equipamento)
  const [collaborators, setCollaborators] = useState([]);
  const [locations, setLocations] = useState([]);
  const [locModalOpen, setLocModalOpen] = useState(false);
  const loadLocations = () => getAssetLocations().then(setLocations).catch(() => setLocations([]));

  const [formData, setFormData] = useState({
    asset_type: "",
    brand: "",
    model: "",
    hostname: "",
    serial_number: "",
    patrimony_code: "",
    invoice_number: "",
    status: "Ativo",
    ownership: "Proprio",
    location: "",
    collaborator_id: "",
    acquisition_date: "",
    notes: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const collabs = await fetchCollaboratorOptions();
      setCollaborators(collabs || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
    loadLocations();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = { ...formData };

      // converte strings vazias em null (para campos opcionais)
      Object.keys(data).forEach((key) => {
        if (data[key] === "") data[key] = null;
      });

      // garante que o id do colaborador seja número quando vier do select
      if (data.collaborator_id != null) data.collaborator_id = Number(data.collaborator_id);

      await api.post("/assets/", data);
      navigate("/assets");
    } catch (error) {
      console.error("Error creating asset:", error);
      alert("Erro ao criar ativo");
    } finally {
      setLoading(false);
    }
  };

  const collaboratorLabel = (c) => {
    const name = `${c.first_name || ""} ${c.last_name || ""}`.trim();
    return name || "Sem nome";
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="new-asset-page">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            onClick={() => navigate("/assets")}
            data-testid="back-to-assets-btn"
            aria-label="Voltar"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="new-asset-title">
              Novo Ativo
            </h1>
            <p className="text-gray-500">Cadastrar novo equipamento</p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Informações do Ativo</CardTitle>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo de Ativo *
                  </label>
                  <Input
                    value={formData.asset_type}
                    onChange={(e) => setFormData({ ...formData, asset_type: e.target.value })}
                    placeholder="Ex: Notebook, Desktop, Monitor"
                    required
                    data-testid="asset-type-input"
                  />
                </div>

                {/* ✅ NOVO: Colaborador */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Colaborador (Usuário do equipamento)
                  </label>
                  <select
                    value={formData.collaborator_id || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, collaborator_id: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="asset-collaborator-select"
                  >
                    <option value="">Não vincular agora</option>
                    {collaborators.map((c) => (
                      <option key={c.id} value={c.id}>
                        {collaboratorLabel(c)}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Esse cadastro é de colaborador (não precisa ser usuário do sistema).
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Marca</label>
                  <Input
                    value={formData.brand}
                    onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                    placeholder="Ex: Dell, HP, Lenovo"
                    data-testid="asset-brand-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Modelo</label>
                  <Input
                    value={formData.model}
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                    placeholder="Ex: Latitude 5420"
                    data-testid="asset-model-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Hostname</label>
                  <Input
                    value={formData.hostname}
                    onChange={(e) => setFormData({ ...formData, hostname: e.target.value })}
                    placeholder="Ex: NB-TI-001"
                    data-testid="asset-hostname-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Número de Série</label>
                  <Input
                    value={formData.serial_number}
                    onChange={(e) =>
                      setFormData({ ...formData, serial_number: e.target.value })
                    }
                    placeholder="Número de série único"
                    data-testid="asset-serial-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Código Patrimonial</label>
                  <Input
                    value={formData.patrimony_code}
                    onChange={(e) =>
                      setFormData({ ...formData, patrimony_code: e.target.value })
                    }
                    placeholder="Código do patrimônio"
                    data-testid="asset-patrimony-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nota Fiscal</label>
                  <Input
                    value={formData.invoice_number}
                    onChange={(e) =>
                      setFormData({ ...formData, invoice_number: e.target.value })
                    }
                    placeholder="Número da NF"
                    data-testid="asset-invoice-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="asset-status-select"
                  >
                    <option value="Ativo">Ativo</option>
                    <option value="Em Estoque">Em Estoque</option>
                    <option value="Em Manutenção">Em Manutenção</option>
                    <option value="Inativo">Inativo</option>
                    <option value="Baixado">Baixado</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Propriedade</label>
                  <select
                    value={formData.ownership}
                    onChange={(e) =>
                      setFormData({ ...formData, ownership: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="asset-ownership-select"
                  >
                    <option value="Proprio">Próprio</option>
                    <option value="Locado">Locado</option>
                  </select>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">Localização</label>
                    <button
                      type="button"
                      onClick={() => setLocModalOpen(true)}
                      className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700"
                    >
                      <Plus size={14} /> Cadastrar localização
                    </button>
                  </div>
                  <select
                    value={formData.location || ""}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="asset-location-input"
                  >
                    <option value="">Selecione uma localização...</option>
                    {formData.location &&
                      !locations.some((l) => l.name === formData.location) && (
                        <option value={formData.location}>{formData.location} (atual)</option>
                      )}
                    {locations.map((l) => (
                      <option key={l.id} value={l.name}>{l.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Data de Aquisição</label>
                  <Input
                    type="date"
                    value={formData.acquisition_date}
                    onChange={(e) =>
                      setFormData({ ...formData, acquisition_date: e.target.value })
                    }
                    data-testid="asset-acquisition-date-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Observações
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Informações adicionais sobre o ativo"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="asset-notes-input"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/assets")}
                  data-testid="cancel-asset-btn"
                >
                  Cancelar
                </Button>
                <Button type="submit" disabled={loading} data-testid="submit-asset-btn">
                  {loading ? "Criando..." : "Criar Ativo"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      <LocationCatalogModal
        isOpen={locModalOpen}
        onClose={() => setLocModalOpen(false)}
        onChanged={loadLocations}
      />
    </Layout>
  );
};

export default NewAsset;
